import React from 'react';

import { Button } from '@components/Button';
import { ErrorMessage } from '@components/Forms/ErrorMessage';
import { TextInput } from '@components/Forms/TextInput';
import { SpinnerModal } from '@components/SpinnerModal';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import type { BookingInquiryMemo } from '@models/BookingInquiry';
import { makeMuiStyles } from '@styles';
import { createValidate, formStrings, stringField } from '@utils/forms';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';
import { Prompt } from 'react-router-dom';

const useStyles = makeMuiStyles((theme, common) => ({
  updatedInfo: {
    ...common.smallerText,
    color: theme.palette.primary.main,
    fontWeight: 500,
    marginTop: '0.1rem',
  },
  input: {
    ...common.smallerText,
  },
  textInput: {
    padding: 5,
  },
  updateButton: {
    alignSelf: 'left',
    width: 60,
    margin: '0.5rem 0',
  },
}));

const memoSchema = t.type({
  memo: stringField(bookingInquiryStrings.memo, 0, 512),
});

const validateMemo = createValidate(memoSchema);

interface MomeFormProps {
  disabled?: boolean;
  isLoading?: boolean;
  onSubmit: (values: BookingInquiryMemo) => Promise<void>;
  memo: string;
  id: number;
}

export const MemoForm: React.FC<MomeFormProps> = (props) => {
  const { memo, isLoading = false, disabled = false, onSubmit } = props;
  const classes = useStyles();

  return (
    <Formik
      initialValues={{
        memo: memo,
      }}
      enableReinitialize
      validate={validateMemo}
      onSubmit={async (values) => {
        await onSubmit({
          memo: values.memo ?? '',
        });
      }}
    >
      {({ dirty, isSubmitting }) => (
        <Form>
          <SpinnerModal open={isLoading} />
          <Prompt when={dirty && !isSubmitting} message={() => formStrings.promptLeaving} />
          <TextInput
            multiline
            noLabel
            name="memo"
            itemVariant="column"
            itemErrorVariant="inline"
            classes={{
              input: classes.input,
              textInput: classes.textInput,
            }}
            disabled={disabled}
          />
          <div style={{ height: 10 }} />
          <ErrorMessage name={'memo'} />
          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.updateButton}
            text={formStrings.update}
            type="submit"
            disabled={disabled}
          />
        </Form>
      )}
    </Formik>
  );
};
